export type DemoVariant = 'business' | 'candidates' | 'mycoach';

export type DemoField =
	| 'first_name'
	| 'last_name'
	| 'business_email'
	| 'phone_number'
	| 'company_name'
	| 'company_website'
	| 'job_title'
	| 'employee_count'
	| 'how_did_you_hear';

export interface DemoForm {
	first_name: string;
	last_name: string;
	business_email: string;
	phone_number: string;
	company_name: string;
	company_website: string;
	job_title: string;
	employee_count: string;
	how_did_you_hear: string;
	consent: boolean;
}

export type DemoErrors = Partial<Record<DemoField | 'consent', string>>;

export const EMPTY_DEMO_FORM: DemoForm = {
	first_name: '',
	last_name: '',
	business_email: '',
	phone_number: '',
	company_name: '',
	company_website: '',
	job_title: '',
	employee_count: '',
	how_did_you_hear: '',
	consent: false
};

export type ProductInterest = 'assessment_service_business' | 'assessment_service_candidates' | 'mycoach';

export const PRODUCT_INTEREST: Record<DemoVariant, ProductInterest> = {
	business: 'assessment_service_business',
	candidates: 'assessment_service_candidates',
	mycoach: 'mycoach'
};

export interface DemoVariantConfig {
	eyebrow: string;
	heading: string;
	subhead: string;
	fields: DemoField[];
}

export const DEMO_VARIANTS: Record<DemoVariant, DemoVariantConfig> = {
	business: {
		eyebrow: 'Assessment Service',
		heading: 'Book a demo',
		subhead: "Tell us about your team and we'll set up a walkthrough tailored to your hiring workflow.",
		fields: [
			'first_name',
			'last_name',
			'business_email',
			'phone_number',
			'company_name',
			'company_website',
			'job_title',
			'employee_count',
			'how_did_you_hear'
		]
	},
	candidates: {
		eyebrow: 'Assessment Service · Candidates',
		heading: 'Book a demo',
		subhead: "See how top firms would read your interviews. We'll walk you through it.",
		fields: ['first_name', 'last_name', 'business_email', 'phone_number']
	},
	mycoach: {
		eyebrow: 'MyCoach (L&D)',
		heading: "Let's talk",
		subhead: "Tell us about your leadership program and we'll show you how MyCoach fits.",
		fields: ['first_name', 'last_name', 'business_email', 'phone_number']
	}
};

// Optional within the field set — every other listed field is required.
const OPTIONAL_FIELDS: DemoField[] = ['how_did_you_hear'];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Loose international phone check: no country context available, so we only
// reject obviously malformed input rather than enforce a specific format.
const PHONE_CHAR_REGEX = /^[+0-9()\-.\s]{7,20}$/;

const URL_REGEX = /^(https?:\/\/)?([\w-]+\.)+[a-z]{2,}(\/\S*)?$/i;

export const validateField = (field: DemoField, form: DemoForm): string | undefined => {
	const value = form[field];
	const trimmed = value.trim();

	if (!OPTIONAL_FIELDS.includes(field) && !trimmed) {
		return 'This field is required';
	}

	if (!trimmed) {
		return undefined;
	}

	if (field === 'business_email' && !EMAIL_REGEX.test(trimmed)) {
		return 'Enter a valid email address';
	}

	if (field === 'phone_number') {
		const digitCount = (trimmed.match(/\d/g) || []).length;
		if (!PHONE_CHAR_REGEX.test(trimmed) || digitCount < 7) {
			return 'Enter a valid phone number';
		}
	}

	if (field === 'company_website' && !URL_REGEX.test(trimmed)) {
		return 'Enter a valid website URL';
	}

	return undefined;
};

export const validateDemoForm = (form: DemoForm, fields: DemoField[]): DemoErrors => {
	const errors: DemoErrors = {};

	fields.forEach(field => {
		const error = validateField(field, form);
		if (error) {
			errors[field] = error;
		}
	});

	if (!form.consent) {
		errors.consent = 'You must agree to the Privacy Policy and Terms of Use';
	}

	return errors;
};

export const isDemoFormValid = (form: DemoForm, fields: DemoField[]): boolean =>
	Object.keys(validateDemoForm(form, fields)).length === 0;

export const FIELD_LABELS: Record<DemoField, string> = {
	first_name: 'First name',
	last_name: 'Last name',
	business_email: 'Business email',
	phone_number: 'Phone number',
	company_name: 'Company name',
	company_website: 'Company website',
	job_title: 'Job title',
	employee_count: 'Employee count',
	how_did_you_hear: 'How did you hear about us? (optional)'
};
